import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import {
  breadcrumbSchema,
  webPageSchema,
  PERSON_ID,
  ORCID_URL,
  ORCID_ID,
  WIKIDATA_URL,
  WIKIDATA_ID,
  NPI,
  NPI_REGISTRY_URL,
  PRESS_FEATURES,
  ANXIETY_RESOURCE_REVIEWER_URL,
  DEPRESSION_RESOURCE_REVIEWER_URL,
  ANXIETY_RESEARCH_REVIEWER_URL,
  PSYCHIATRY_RX_REVIEWER_URL,
} from "@/lib/seo";

const TITLE = "Independent Verification | Shariq Refai, MD, MBA";
const DESCRIPTION =
  "Independent ways to verify Shariq Refai, MD, MBA, FAPA: ORCID, Wikidata, the NPI Registry, peer reviewed work, press, and the Shrink Network's own records.";
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Verified", path: "/verified" },
];

type Row = { label: string; href: string; note: string };

const IDENTIFIERS: Row[] = [
  {
    label: `ORCID ${ORCID_ID}`,
    href: ORCID_URL,
    note: "Researcher identifier, maintained by ORCID.",
  },
  {
    label: `Wikidata ${WIKIDATA_ID}`,
    href: WIKIDATA_URL,
    note: "Public knowledge graph entry for Shariq Refai.",
  },
  {
    label: `NPI ${NPI}`,
    href: NPI_REGISTRY_URL,
    note: "National Provider Identifier record in the CMS NPI Registry.",
  },
];

const PROFILES: Row[] = [
  {
    label: "Doximity",
    href: "https://www.doximity.com/pub/shariq-refai-md",
    note: "Physician network profile.",
  },
  {
    label: "Healthgrades",
    href: "https://www.healthgrades.com/physician/dr-shariq-refai-xfq4b",
    note: "Physician directory profile.",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/shariqrefai",
    note: "Professional profile.",
  },
  {
    label: "shrinkMD team page",
    href: "https://shrinkmd.com/shariq-refai",
    note: "Clinical practice profile.",
  },
];

const PEER_REVIEWED: Row[] = [
  {
    label: "A Case of Priapism Associated With Rapid Increase in Risperidone Dose (2012)",
    href: "https://doi.org/10.4088/PCC.12l01365",
    note: "Primary Care Companion for CNS Disorders. DOI 10.4088/PCC.12l01365.",
  },
];

const NETWORK: Row[] = [
  {
    label: "The Shrink Network (Wikidata Q139972275)",
    href: "https://www.wikidata.org/wiki/Q139972275",
    note: "The network of sites Dr. Refai founded and medically reviews.",
  },
  {
    label: "shrinkMD (Wikidata Q139976539)",
    href: "https://www.wikidata.org/wiki/Q139976539",
    note: "The telepsychiatry practice he founded.",
  },
  {
    label: "shrinQ (Wikidata Q139984236)",
    href: "https://www.wikidata.org/wiki/Q139984236",
    note: "The structured educational program he founded.",
  },
  {
    label: "AnxietyResource.org reviewer page",
    href: ANXIETY_RESOURCE_REVIEWER_URL,
    note: "Named medical reviewer.",
  },
  {
    label: "DepressionResource.org reviewer page",
    href: DEPRESSION_RESOURCE_REVIEWER_URL,
    note: "Named medical reviewer.",
  },
  {
    label: "AnxietyResearch.org review board",
    href: ANXIETY_RESEARCH_REVIEWER_URL,
    note: "Named medical reviewer.",
  },
  {
    label: "PsychiatryRx.org medical editor",
    href: PSYCHIATRY_RX_REVIEWER_URL,
    note: "Named medical editor.",
  },
];

const STANDARDS: { label: string; path: string; note: string }[] = [
  { label: "Editorial process", path: "/editorial-process", note: "How content is researched, written, and reviewed." },
  { label: "Evidence standards", path: "/evidence", note: "How claims are sourced and labeled." },
  { label: "Corrections", path: "/corrections", note: "How errors are fixed and logged." },
  { label: "Disclosures", path: "/disclosures", note: "Financial interests and conflicts." },
  { label: "AI use policy", path: "/ai-policy", note: "Where AI tools are and aren't used." },
  { label: "Medical review board", path: "/medical-review-board", note: "Who reviews clinical content." },
];

const FILES: Row[] = [
  { label: "llms.txt", href: "/llms.txt", note: "Curated site map for language models." },
  { label: "llms-full.txt", href: "/llms-full.txt", note: "Full text of the core pages in one file." },
  { label: "ai.txt", href: "/ai.txt", note: "Publisher, reviewer, license, and AI usage terms." },
  { label: "index.json", href: "/index.json", note: "Network registry manifest for this site." },
  { label: "rss.xml", href: "/rss.xml", note: "Essay feed (RSS 2.0)." },
  { label: "feed.json", href: "/feed.json", note: "Essay feed (JSON Feed 1.1)." },
  { label: "sitemap-index.xml", href: "/sitemap-index.xml", note: "Every sitemap for this site." },
  { label: "security.txt", href: "/.well-known/security.txt", note: "Security contact (RFC 9116)." },
];

const CITE_SHORT = "Shariq Refai, MD, MBA, FAPA, board-certified psychiatrist (shariqrefai.com).";
const CITE_APA =
  "Refai, S. (2026). Independent verification. Shariq Refai, MD, MBA. https://shariqrefai.com/verified/";
const CITE_MLA =
  "Refai, Shariq. “Independent Verification.” Shariq Refai, MD, MBA, 2026, shariqrefai.com/verified/.";

function ExternalList({ rows }: { rows: Row[] }) {
  return (
    <ul>
      {rows.map((r) => (
        <li key={r.href}>
          <a href={r.href} target="_blank" rel="noopener noreferrer">
            {r.label}
          </a>
          . {r.note}
        </li>
      ))}
    </ul>
  );
}

export default function Verified() {
  const page = {
    ...webPageSchema({
      path: "/verified",
      title: TITLE,
      description: DESCRIPTION,
      breadcrumbs: CRUMBS,
      speakableSelectors: [".verify-summary"],
      datePublished: "2026-09-23",
      dateModified: "2026-09-23",
      lastReviewed: "2026-09-23",
    }),
    mainEntity: { "@id": PERSON_ID },
  };

  return (
    <SiteLayout>
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        url="/verified"
        type="profile"
        schema={[page, breadcrumbSchema(CRUMBS)]}
      />

      <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Link href="/" className="hover:text-accent transition-colors">
              Home
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">Verified</span>
          </nav>
          <p className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-accent">
            Independent verification · The Shrink Network
          </p>
          <h1 className="mt-4 text-4xl md:text-5xl font-serif text-primary leading-snug">
            Verify Shariq Refai, MD, MBA without taking our word for it.
          </h1>
          <p className="verify-summary speakable mt-4 text-lg text-muted-foreground font-light max-w-2xl">
            Shariq Refai, MD, MBA, FAPA is a board-certified psychiatrist and
            the founder and medical reviewer of The Shrink Network. Every link
            below goes to a record kept by someone else, so you can check who
            he is, what he's published, and which sites he reviews.
          </p>
        </div>
      </header>

      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary">
          <p>
            Every Shrink Network site links here when it names Dr. Refai as
            author or medical reviewer. If a page anywhere claims his review
            and it isn't one of the sites listed below, please tell us at{" "}
            <a href="mailto:editor@shariqrefai.com">editor@shariqrefai.com</a>.
          </p>

          <h2>Identifiers</h2>
          <ExternalList rows={IDENTIFIERS} />

          <h2>Professional profiles</h2>
          <ExternalList rows={PROFILES} />

          <h2>Peer-reviewed work</h2>
          <ExternalList rows={PEER_REVIEWED} />

          <h2>Press and interviews</h2>
          <ul>
            {PRESS_FEATURES.map((p) => (
              <li key={p.url}>
                <a href={p.url} target="_blank" rel="noopener noreferrer">
                  {p.headline}
                </a>
                . {p.publisher}
                {p.datePublished ? `, ${p.datePublished.slice(0, 4)}` : ""}.
              </li>
            ))}
          </ul>
          <p>
            The full list, with context, is on the{" "}
            <Link href="/media">media page</Link>.
          </p>

          <h2>The Shrink Network</h2>
          <ExternalList rows={NETWORK} />

          <h2>Editorial and evidence standards</h2>
          <ul>
            {STANDARDS.map((s) => (
              <li key={s.path}>
                <Link href={s.path}>{s.label}</Link>. {s.note}
              </li>
            ))}
          </ul>

          <h2>How to cite</h2>
          <p>Short:</p>
          <pre className="whitespace-pre-wrap select-all text-sm">{CITE_SHORT}</pre>
          <p>APA:</p>
          <pre className="whitespace-pre-wrap select-all text-sm">{CITE_APA}</pre>
          <p>MLA:</p>
          <pre className="whitespace-pre-wrap select-all text-sm">{CITE_MLA}</pre>

          <h2>Machine-readable files</h2>
          <ul>
            {FILES.map((f) => (
              <li key={f.href}>
                <a href={f.href}>{f.label}</a>. {f.note}
              </li>
            ))}
          </ul>
        </div>
      </article>
    </SiteLayout>
  );
}
